import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/dashboard.css';
import { useAppId } from '../context/AppIdContext';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Dashboard = () => {
    const navigate = useNavigate();
    const {
        appId,
        total_voters,
        language,
        vidhanSabhaName,
        vidhanSabhaNo,
        userType // Fetching userType from context
    } = useAppId();

    const lang = language || 'en';

    // --- ROLE VERIFICATION LOGIC ---
    // Convert to uppercase to match your exact role codes
    const roleCode = userType?.toUpperCase() || 'A';

    const isAdmin = roleCode === 'A' || roleCode === 'ADMIN';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';
    const isShaktiKendra = roleCode === 'SK' || roleCode === 'SHAKTI_KENDRA';
    const isBoothPramukh = roleCode === 'BP' || roleCode === 'BOOTH_PRAMUKH';
    const isSahBoothPramukh = roleCode === 'BS' || roleCode === 'SAH_BOOTH_PRAMUKH';
    const isKaryakarta = roleCode === 'K' || roleCode === 'KARYAKARTA';
    const isCallCenter = roleCode === 'CL' || roleCode === 'CALL_CENTER';

    useEffect(() => {
        if (!appId) {
            navigate('/')
        }
    }, [appId, navigate]);

    // Translation dictionary
    const t = {
        en: {
            // Header
            totalVoters: "Total Voters :",
            vidhanSabha: "Vidhan Sabha :",

            // Section Titles
            cadres: "Cadres",
            reports: "Reports",
            communication: "Communication & Groups",

            // Card Titles
            admin: "Admin",
            subAdmin: "Sub Admin",
            shaktiKendra: "Shakti Kendra",
            boothPramukh: "Booth Pramukh",
            sahBoothPramukh: "Sah Booth Pramukh",
            volunteer: "Volunteer",
            callCenter: "Call Center",
            voterList: "Voter List",
            voterListSubtitle: "Search and manage voter database",

            // BP Specific Cards
            sangathan: "Organization",
            matdar: "Voter",
            karyakartaBP: "Volunteer",
            survey: "Survey",

            // Report Cards
            slipSendingReport: "Slip Sending Report",
            cadreWiseSurvey: "Cadre Wise Survey Report",
            callCenterSurvey: "Call Center Survey Report",
            starVolunteer: "Star Volunteer",

            // Group Cards
            myPhonebook: "My Phonebook",
            volunteerPhonebook: "Volunteer's Phonebook",
            myGroup: "My Group",
            volunteerGroup: "Volunteer's Group",
            surnameGroup: "Surname Group",
            volSurnameGroup: "Vol. Surname Group",

            // Loading
            loading: "Loading..."
        },

        hi: {
            // Header
            totalVoters: "कुल मतदाता :",
            vidhanSabha: "विधानसभा :",

            // Section Titles
            cadres: "कार्यकर्ता",
            reports: "रिपोर्ट्स",
            communication: "संचार और समूह",

            // Card Titles
            admin: "एडमिन",
            subAdmin: "उप एडमिन",
            shaktiKendra: "शक्ति केंद्र",
            boothPramukh: "बूथ प्रमुख",
            sahBoothPramukh: "सह बूथ प्रमुख",
            volunteer: "स्वयंसेवक",
            callCenter: "कॉल सेंटर",
            voterList: "मतदाता सूची",
            voterListSubtitle: "मतदाता डेटाबेस खोजें और प्रबंधित करें",

            // BP Specific Cards
            sangathan: "संगठन",
            matdar: "मतदाता",
            karyakartaBP: "कार्यकर्ता",
            survey: "सर्वेक्षण",

            // Report Cards
            slipSendingReport: "स्लिप भेजने की रिपोर्ट",
            cadreWiseSurvey: "कार्यकर्ता वार सर्वेक्षण रिपोर्ट",
            callCenterSurvey: "कॉल सेंटर सर्वेक्षण रिपोर्ट",
            starVolunteer: "स्टार स्वयंसेवक",

            // Group Cards
            myPhonebook: "मेरी फोनबुक",
            volunteerPhonebook: "स्वयंसेवक की फोनबुक",
            myGroup: "मेरा समूह",
            volunteerGroup: "स्वयंसेवक का समूह",
            surnameGroup: "उपनाम समूह",
            volSurnameGroup: "स्व. उपनाम समूह",

            // Loading
            loading: "लोड हो रहा है..."
        },

        gu: {
            // Header
            totalVoters: "કુલ મતદારો :",
            vidhanSabha: "વિધાનસભા :",

            // Section Titles
            cadres: "કાર્યકર્તાઓ",
            reports: "રિપોર્ટ્સ",
            communication: "સંચાર અને જૂથો",

            // Card Titles
            admin: "એડમિન",
            subAdmin: "પેટા એડમિન",
            shaktiKendra: "શક્તિ કેન્દ્ર",
            boothPramukh: "બૂથ પ્રમુખ",
            sahBoothPramukh: "સહ બૂથ પ્રમુખ",
            volunteer: "સ્વયંસેવક",
            callCenter: "કૉલ સેન્ટર",
            voterList: "મતદાર યાદી",
            voterListSubtitle: "મતદાર ડેટાબેઝ શોધો અને સંચાલિત કરો",

            // BP Specific Cards
            sangathan: "સંગઠન",
            matdar: "મતદાર",
            karyakartaBP: "કાર્યકર્તા",
            survey: "સર્વે",

            // Report Cards
            slipSendingReport: "સ્લિપ મોકલવાનો રિપોર્ટ",
            cadreWiseSurvey: "કાર્યકર્તા વાઈઝ સર્વે રિપોર્ટ",
            callCenterSurvey: "કૉલ સેન્ટર સર્વે રિપોર્ટ",
            starVolunteer: "સ્ટાર સ્વયંસેવક",

            // Group Cards
            myPhonebook: "મારી ફોનબુક",
            volunteerPhonebook: "સ્વયંસેવકની ફોનબુક",
            myGroup: "મારું જૂથ",
            volunteerGroup: "સ્વયંસેવકનું જૂથ",
            surnameGroup: "ઉપનામ જૂથ",
            volSurnameGroup: "સ્વ. ઉપનામ જૂથ",

            // Loading
            loading: "લોડ થઈ રહ્યું છે..."
        }
    };

    const txt = t[lang];

    // --- Navigation Handlers ---
    const handleNavigate = (path) => {
        navigate(path);
    };

    return (
        <div className="app-container dashboard-page">
            <div className="dashboard-content container-fluid p-4">

                {/* --- Header Component --- */}
                <Header title="Dashboard" />

                {/* --- Vidhan Sabha and Total Voters Info in White Card --- */}
                <div className="info-card mb-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                    <div className="info-card-content">
                        {/* Left Side: Vidhan Sabha Number & Name */}
                        <div className="info-item">
                            <span className="info-label">{txt.vidhanSabha}</span>
                            <div className="info-value-group">
                                {vidhanSabhaNo && (
                                    <span className="info-number">{vidhanSabhaNo}</span>
                                )}
                                <span className="info-name">{vidhanSabhaName || txt.loading}</span>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="info-divider"></div>

                        {/* Right Side: Total Voters */}
                        <div className="info-item">
                            <span className="info-label">{txt.totalVoters}</span>
                            <div className="info-value-group">
                                <span className="info-number-large">{total_voters?.toLocaleString()}</span>

                            </div>
                        </div>
                    </div>
                </div>

                {/* --- BOOTH PRAMUKH SPECIFIC DASHBOARD VIEW --- */}
                {isBoothPramukh ? (
                    <>
                        <div className="row g-4 mb-4 slide-up-element" style={{ animationDelay: '0.2s', marginTop: '10px' }}>
                            {/* Card 1: Sangathan */}
                            <Dashboard_card
                                title={txt.sangathan}
                                icon={<ThreePeopleIcon color="currentColor" />}
                                color="#2563eb"
                                delay="0.3s"
                                onClick={() => handleNavigate('#')}

                            />

                            {/* Card 2: Matdar (Linked to Voter List) */}
                            <Dashboard_card
                                title={txt.matdar}
                                icon={<ThreePeopleIcon color="currentColor" />}
                                color="#f97316"
                                delay="0.35s"
                                onClick={() => handleNavigate('/voter_list')}
                            />

                            {/* Card 3: Karyakarta (Linked to Volunteers) */}
                            <Dashboard_card
                                title={txt.karyakartaBP}
                                icon={<MultiPeopleIcon />}
                                color="#ec4899"
                                delay="0.4s"
                                onClick={() => handleNavigate('/display_karyakarta')}
                            />
                        </div>

                        {/* Voter List Banner for BP */}
                        <div className="row mb-4 fade-in-up" style={{ animationDelay: '0.5s' }}>
                            <div className="col-12">
                                <div className="voter-search-card" onClick={() => handleNavigate('/voter_list')}>
                                    <div className="voter-icon-circle">
                                        <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                    </div>
                                    <div className="voter-text">
                                        <h3>{txt.voterList}</h3>
                                        <p>{txt.voterListSubtitle}</p>
                                    </div>
                                    <div className="arrow-icon">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reports Section for BP */}
                        <h4 className="section-title fade-in-up" style={{ animationDelay: '0.6s' }}>{txt.reports}</h4>
                        <div className="row g-4 mb-4">
                            <ReportCard
                                title={txt.slipSendingReport}
                                icon={<ReportIcon />}
                                color="#22c55e"
                                delay="0.7s"
                                onClick={() => { handleNavigate('/get_total_slip_distribution_count') }}
                            />
                            <ReportCard
                                title={txt.survey}
                                icon={<SurveyIcon />}
                                color="#f97316"
                                delay="0.75s"
                                onClick={() => { handleNavigate('/dis_survey_dash_for_admin') }}
                            />
                        </div>

                        {/* Communication Section for BP */}
                        <h4 className="section-title fade-in-up" style={{ animationDelay: '0.8s' }}>{txt.communication}</h4>
                        <div className="row g-4 mb-5">
                            {/* <CombinedDashboardCard
                                title1={txt.myPhonebook}
                                icon1="📖"
                                onClick1={() => handleNavigate('/dis_contact_group_member')}
                                title2={txt.surnameGroup}
                                icon2="👪"
                                onClick2={() => handleNavigate('/dis_surname_group')}
                                delay="0.9s"
                            /> */}
                            <div className="col-12 col-md-6 col-lg-4 fade-in-up" style={{ animationDelay: '1.0s' }}>
                                <div className="combined-card">
                                    <div className="combined-row" onClick={() => handleNavigate('/dis_contact_group_member')}>
                                        <span className="list-icon">📖</span>
                                        <span className="list-title">{txt.myPhonebook}</span>
                                        <svg className="list-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 fade-in-up" style={{ animationDelay: '1.0s' }}>
                                <div className="combined-card">
                                    <div className="combined-row" onClick={() => handleNavigate('/dis_surname_group')}>
                                        <span className="list-icon">👪</span>
                                        <span className="list-title">{txt.surnameGroup}</span>
                                        <svg className="list-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 fade-in-up" style={{ animationDelay: '1.0s' }}>
                                <div className="combined-card">
                                    <div className="combined-row" onClick={() => handleNavigate('/dis_my_group_member')}>
                                        <span className="list-icon">👥</span>
                                        <span className="list-title">{txt.myGroup}</span>
                                        <svg className="list-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </>
                ) : (
                    <>
                        {/* --- REGULAR DASHBOARD VIEW (Non-BP) --- */}

                        {/* --- Cadres Section --- */}
                        {(isAdmin || isSubAdmin || isShaktiKendra) && (
                            <>
                                <h4 className="section-title fade-in-up" style={{ animationDelay: '0.2s', marginTop: '10px' }}>{txt.cadres}</h4>
                                <div className="row g-4 mb-5">

                                    {/* Only Admin sees Admin */}
                                    {isAdmin && (
                                        <DashboardCard
                                            title={txt.admin}
                                            icon={<UserGearIcon />}
                                            color="#0ea5e9"
                                            delay="0.3s"
                                            onClick={() => handleNavigate('/admin')}
                                        />
                                    )}

                                    {/* Admin and Subadmin see SubAdmin */}
                                    {(isAdmin) && (
                                        <DashboardCard
                                            title={txt.subAdmin}
                                            icon={<UserGroupIcon />}
                                            color="#f59e0b"
                                            delay="0.35s"
                                            onClick={() => handleNavigate('/sub_admin')}
                                        />
                                    )}

                                    {/* Higher roles see Shakti Kendra */}
                                    {(isAdmin || isSubAdmin) && (
                                        <DashboardCard
                                            title={txt.shaktiKendra}
                                            subtitle="Pramukh"
                                            icon={<BuildingIcon />}
                                            color="#10b981"
                                            delay="0.4s"
                                            onClick={() => handleNavigate('/dis_saktikendra')}
                                        />
                                    )}

                                    {/* Higher roles see Booth Pramukh */}
                                    {(isAdmin || isSubAdmin || isShaktiKendra) && (
                                        <DashboardCard
                                            title={txt.boothPramukh}
                                            icon={<BoothIcon />}
                                            color="#6366f1"
                                            delay="0.45s"
                                            onClick={() => handleNavigate('/booth_pramukh')}
                                        />
                                    )}

                                    {/* Higher roles see Volunteer */}
                                    {(isAdmin || isSubAdmin || isShaktiKendra) && (
                                        <DashboardCard
                                            title={txt.volunteer}
                                            icon={<VolunteerIcon />}
                                            color="#ec4899"
                                            delay="0.5s"
                                            onClick={() => handleNavigate('/display_karyakarta')}
                                        />
                                    )}

                                    {/* Call center card */}
                                    {(isAdmin) && (
                                        <DashboardCard
                                            title={txt.callCenter}
                                            icon={<HeadsetIcon />}
                                            color="#8b5cf6"
                                            delay="0.55s"
                                            onClick={() => handleNavigate('/dis_call_center_user')}
                                        />
                                    )}
                                </div>
                            </>
                        )}

                        {/* --- Search Banner --- */}
                        <div className="row mb-5 fade-in-up" style={{ animationDelay: '0.6s' }}>
                            <div className="col-12">
                                <div className="voter-search-card" onClick={() => handleNavigate('/voter_list')}>
                                    <div className="voter-icon-circle">
                                        <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                    </div>
                                    <div className="voter-text">
                                        <h3>{txt.voterList}</h3>
                                        <p>{txt.voterListSubtitle}</p>
                                    </div>
                                    <div className="arrow-icon">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- Reports Section --- */}
                        {(isAdmin || isSubAdmin) && (
                            <>
                                <h4 className="section-title fade-in-up" style={{ animationDelay: '0.65s' }}>{txt.reports}</h4>
                                <div className="row g-4 mb-5">
                                    <ReportCard
                                        title={txt.slipSendingReport}
                                        icon={<ReportIcon />}
                                        color="#22c55e"
                                        delay="0.7s"
                                        onClick={() => { handleNavigate('/get_total_slip_distribution_count') }}
                                    />
                                    <ReportCard
                                        title={txt.cadreWiseSurvey}
                                        icon={<SurveyIcon />}
                                        color="#f97316"
                                        delay="0.75s"
                                        onClick={() => { handleNavigate('/dis_survey_dash_for_admin') }}
                                    />
                                    <ReportCard
                                        title={txt.callCenterSurvey}
                                        icon={<HeadsetReportIcon />}
                                        color="#3b82f6"
                                        delay="0.8s"
                                        onClick={() => { handleNavigate('/dis_admin_call_center_survey_dashboard') }}
                                    />
                                </div>
                            </>
                        )}

                        {/* --- Groups List --- */}
                        {(isAdmin || isSubAdmin) && (
                            <>
                                <h4 className="section-title fade-in-up" style={{ animationDelay: '0.85s' }}>{txt.communication}</h4>
                                <div className="row g-4 mb-5">

                                    {/* Pair 1: Phonebooks */}
                                    <CombinedDashboardCard
                                        title1={txt.myPhonebook}
                                        icon1="📖"
                                        onClick1={() => handleNavigate('/dis_contact_group_member')}
                                        title2={txt.volunteerPhonebook}
                                        icon2="📒"
                                        onClick2={() => handleNavigate('/dis_all_contact_match_admin')}
                                        delay="0.9s"
                                    />

                                    {/* Pair 2: General Groups */}
                                    <CombinedDashboardCard
                                        title1={txt.myGroup}
                                        icon1="👥"
                                        onClick1={() => handleNavigate('/dis_my_group_member')}
                                        title2={txt.volunteerGroup}
                                        icon2="🧑‍🤝‍🧑"
                                        onClick2={() => handleNavigate('/dis_user_list_by_create_group')}
                                        delay="1.0s"
                                    />

                                </div>
                            </>
                        )}

                        {/* --- Additional Reports --- */}
                        {(isAdmin || isSubAdmin) && (
                            <div className="row g-4 mb-5">
                                <ReportCard
                                    title='Prachar Master'
                                    icon={<SurveyIcon />}
                                    color="#f97316"
                                    delay="0.75s"
                                    onClick={() => { handleNavigate('/prachar_master') }}
                                />
                                <ReportCard
                                    title={txt.starVolunteer}
                                    icon={<HeadsetReportIcon />}
                                    color="#3b82f6"
                                    delay="0.8s"
                                    onClick={() => { handleNavigate('/dis_star_karykarta') }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Inline Styles */}
            <style jsx>{`
                /* --- White Info Card Styles --- */
                .info-card {
                    background: white;
                    border-radius: 16px;
                    padding: 20px 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                }
                
                .info-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    transform: translateY(-1px);
                }
                
                .info-card-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 20px;
                }
                
                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .info-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .info-value-group {
                    display: flex;
                    align-items: baseline;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                
                .info-number {
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #ea580c;
                    background: #fff7ed;
                    padding: 4px 10px;
                    border-radius: 8px;
                }
                
                .info-name {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #1e293b;
                }
                
                .info-number-large {
                    font-size: 1.75rem;
                    font-weight: 800;
                    color: #0369a1;
                    background: #e0f2fe;
                    padding: 4px 12px;
                    border-radius: 10px;
                    line-height: 1.2;
                }
                
                .info-unit {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #64748b;
                }
                
                .info-divider {
                    width: 1px;
                    height: 50px;
                    background: #e2e8f0;
                }
                
                .report-card {
                    background: white;
                    border-radius: 12px;
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .report-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
                }
                .report-icon-box {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .report-title {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #334155;
                    line-height: 1.3;
                }

                /* --- STYLES FOR COMBINED CARD --- */
                .combined-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                .combined-row {
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    transition: background 0.2s;
                    flex: 1;
                }
                .combined-row:hover {
                    background-color: #f8fafc;
                }
                .combined-divider {
                    height: 1px;
                    background-color: #f1f5f9;
                    width: 100%;
                }
                .list-icon {
                    font-size: 1.4rem;
                    margin-right: 14px;
                    display: flex;
                    align-items: center;
                }
                .list-title {
                    flex-grow: 1;
                    font-weight: 600;
                    color: #334155;
                    font-size: 1rem;
                }
                .list-arrow {
                    color: #cbd5e1;
                    transition: color 0.2s;
                }
                .combined-row:hover .list-arrow {
                    color: #64748b;
                }
                     /* Mobile Responsive Fixes */
                @media (max-width: 768px) {
                    .info-card-content {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 16px;
                    }
                    
                    .info-divider {
                        width: 100%;
                        height: 1px;
                    }
                    
                    .info-item {
                        align-items: center;
                        text-align: center;
                    }
                    
                    .info-value-group {
                        justify-content: center;
                    }
                    
                    .info-number-large {
                        font-size: 1.4rem;
                    }
                    
                    .voter-search-card {
                        padding: 16px 20px;
                        border-radius: 16px;
                    }
                    
                    .voter-icon-circle {
                        width: 44px;
                        height: 44px;
                        margin-right: 12px;
                    }
                    
                    .voter-icon-circle svg {
                        width: 22px;
                        height: 22px;
                    }
                    
                    .voter-text h3 {
                        font-size: 1rem;
                    }
                    
                    .voter-text p {
                        font-size: 0.75rem;
                    }
                    
                    .arrow-icon svg {
                        width: 20px;
                        height: 20px;
                    }
                }
                
                @media (max-width: 480px) {
                    .info-card {
                        padding: 16px;
                    }
                    
                    .info-number {
                        font-size: 1rem;
                    }
                    
                    .info-name {
                        font-size: 0.95rem;
                    }
                    
                    .info-number-large {
                        font-size: 1.2rem;
                    }
                    
                    .voter-search-card {
                        padding: 14px 16px;
                        border-radius: 14px;
                    }
                    
                    .voter-icon-circle {
                        width: 38px;
                        height: 38px;
                        margin-right: 10px;
                    }
                    
                    .voter-icon-circle svg {
                        width: 18px;
                        height: 18px;
                    }
                    
                    .voter-text h3 {
                        font-size: 0.9rem;
                    }
                    
                    .voter-text p {
                        font-size: 0.7rem;
                    }
                    
                    .arrow-icon svg {
                        width: 18px;
                        height: 18px;
                    }
                }
                
            `}</style>
        </div>
    );
};

// --- Helper Components ---

const DashboardCard = ({ title, subtitle, icon, color, onClick, delay }) => (
    <div className="col-6 col-md-4 col-lg-2 fade-in-up" style={{ animationDelay: delay }}>
        <div className="dash-card main-card h-100" onClick={onClick} style={{ '--hover-color': color }}>
            <div className="icon-wrapper" style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            <h3 className="card-title">{title}</h3>
            {subtitle && <span className="card-subtitle">{subtitle}</span>}
        </div>
    </div>
);


const Dashboard_card = ({ title, subtitle, icon, color, onClick, delay }) => (
    <div className="col-6 col-md-4 col-lg-4 fade-in-up" style={{ animationDelay: delay }}>
        <div className="dash-card main-card h-100" onClick={onClick} style={{ '--hover-color': color }}>
            <div className="icon-wrapper" style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            <h3 className="card-title">{title}</h3>
            {subtitle && <span className="card-subtitle">{subtitle}</span>}
        </div>
    </div>
);

const ReportCard = ({ title, icon, color, onClick, delay }) => (
    <div className="col-12 col-md-4 fade-in-up" style={{ animationDelay: delay }}>
        <div className="report-card" onClick={onClick}>
            <div className="report-icon-box" style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            <h5 className="report-title">{title}</h5>
        </div>
    </div>
);

const CombinedDashboardCard = ({ title1, icon1, onClick1, title2, icon2, onClick2, delay }) => (
    <div className="col-12 col-md-6 col-lg-4 fade-in-up" style={{ animationDelay: delay }}>
        <div className="combined-card">
            {/* Top Item */}
            <div className="combined-row" onClick={onClick1}>
                <span className="list-icon">{icon1}</span>
                <span className="list-title">{title1}</span>
                <svg className="list-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </div>

            {/* Divider */}
            <div className="combined-divider"></div>

            {/* Bottom Item */}
            <div className="combined-row" onClick={onClick2}>
                <span className="list-icon">{icon2}</span>
                <span className="list-title">{title2}</span>
                <svg className="list-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </div>
        </div>
    </div>
);

// --- Custom SVG Icons for Booth Pramukh Layout ---
const ThreePeopleIcon = ({ color }) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
        {/* Background People */}
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
        {/* Front Person */}
        <path d="M12 13c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
);

const MultiPeopleIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24">
        {/* Left Background Person - Gray */}
        <path fill="#94a3b8" d="M8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" />
        {/* Right Background Person - Light Blue */}
        <path fill="#7dd3fc" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm0 2c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
        {/* Front Person - Pink/Red */}
        <path fill="#e11d48" d="M12 13c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
);

// --- Existing Icons ---
const UserGearIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const UserGroupIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
const BuildingIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
const BoothIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>;
const VolunteerIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>;
const HeadsetIcon = () => <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>;
const ReportIcon = () => <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>;
const SurveyIcon = () => <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const HeadsetReportIcon = () => <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;

export default Dashboard;